import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dateProperty"],
  _excluded2 = ["dateProperty"];
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
import I18n from 'I18n';
import { ONCE, DAILY, WEEKLY, MONTHLY, YEARLY, ScheduleWidowTimeRanges } from 'automation-ui-enrollment/constants/scheduledEnrollment/ScheduleCadence';
import { VALUES, EVERY, NOT_APPLICABLE, DELEGATE_TO_PROPERTY_VALUE } from 'automation-ui-enrollment/constants/scheduledEnrollment/ExpressionTypes';
import { MONDAY } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfWeekValues';
import { TranslatedErrorMessageKey, MISSING_DAY_OF_MONTH_FIELD, MISSING_DAY_OF_WEEK_FIELD } from 'automation-ui-enrollment/constants/scheduledEnrollment/InvalidScheduleReasons';
import { DayOfMonthFlexValues } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfMonthValues';
import EnrollmentDateProperty from 'automation-ui-enrollment/records/EnrollmentDateProperty';
/*
  - Schedules are represented as a `month`, `dayOfWeek` or `dayOfMonth`, and time (`hour`, `minute`)
  - We will derive the `cadence` value from the properties stored on the BE
  - Schedules may not have both `dayOfMonth`, and `dayOfWeek` properties set
  - "Not applicable" is not a valid type for the `month` property
*/
export default class EnrollmentSchedule extends createImmutableObjectClass({
  minute: {
    expressionType: VALUES,
    values: [0]
  },
  hour: {
    expressionType: VALUES,
    values: [8]
  },
  month: {
    expressionType: EVERY
  },
  dayOfMonth: {
    expressionType: NOT_APPLICABLE
  },
  dayOfWeek: {
    expressionType: EVERY
  },
  year: {
    expressionType: EVERY
  },
  dateProperty: undefined,
  scheduleWindow: 'SPECIFIC_TIME'
}, {
  fromJSON: _ref => {
    let {
        dateProperty
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      dateProperty: dateProperty && new EnrollmentDateProperty(dateProperty)
    }, rest);
  },
  toJSON: _ref2 => {
    let {
        dateProperty
      } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    return Object.assign({
      dateProperty: dateProperty === null || dateProperty === void 0 ? void 0 : dateProperty.toJSON()
    }, rest);
  }
}) {
  toEnrollmentScheduleInterface() {
    return this.toJSON();
  }
  setDayOfWeekValues(values) {
    return this.set('dayOfWeek', {
      expressionType: VALUES,
      values
    });
  }
  setDayOfMonthValues(values) {
    return this.set('dayOfMonth', {
      expressionType: VALUES,
      values
    }).set('dayOfWeek', {
      expressionType: NOT_APPLICABLE
    });
  }
  setMonthValues(values) {
    return this.set('month', {
      expressionType: VALUES,
      values
    });
  }
  setYearValues(values) {
    return this.set('year', {
      expressionType: VALUES,
      values
    });
  }
  getScheduleWindow() {
    return this.scheduleWindow;
  }
  setScheduleWindow(scheduleWindow) {
    let newSchedule = this.set('scheduleWindow', scheduleWindow);
    if (scheduleWindow !== 'SPECIFIC_TIME') {
      newSchedule = newSchedule.setTimeFromMinutes(ScheduleWidowTimeRanges[scheduleWindow].start * 60);
    }
    return newSchedule;
  }
  setSimpleDate({
    year,
    month,
    date
  }) {
    return this.setYearValues([year]).setMonthValues([month + 1]).setDayOfMonthValues([date]);
  }
  setDateProperty(propertyName) {
    const yearExpressionType = this.getCadence() === ONCE ? DELEGATE_TO_PROPERTY_VALUE : EVERY;
    return this.set('dateProperty', new EnrollmentDateProperty({
      propertyName
    })).set('year', {
      expressionType: yearExpressionType
    }).set('month', {
      expressionType: DELEGATE_TO_PROPERTY_VALUE
    }).set('dayOfMonth', {
      expressionType: DELEGATE_TO_PROPERTY_VALUE
    });
  }
  setTimeDelta(timeDelta) {
    return this.update('dateProperty', dateProperty => dateProperty === null || dateProperty === void 0 ? void 0 : dateProperty.setTimeDelta(timeDelta));
  }

  /* Cadence is a derived property, which classifies the schedule into one of the following categories
    - Once
    - Daily
    - Weekly
    - Monthly
    - Yearly
   */
  setCadence(cadence) {
    if (cadence === ONCE) {
      const tomorrow = I18n.moment().add(1, 'day');
      return this.set('year', {
        expressionType: VALUES,
        values: [tomorrow.year()]
      }).set('month', {
        expressionType: VALUES,
        values: [tomorrow.month() + 1]
      }).set('dayOfMonth', {
        expressionType: VALUES,
        values: [tomorrow.date()]
      }).set('dayOfWeek', {
        expressionType: NOT_APPLICABLE
      }).set('dateProperty', undefined);
    } else if (cadence === DAILY) {
      return this.set('year', {
        expressionType: EVERY
      }).set('month', {
        expressionType: EVERY
      }).set('dayOfMonth', {
        expressionType: NOT_APPLICABLE
      }).set('dayOfWeek', {
        expressionType: EVERY
      }).set('dateProperty', undefined);
    } else if (cadence === WEEKLY) {
      /* default to monday */
      return this.set('year', {
        expressionType: EVERY
      }).set('month', {
        expressionType: EVERY
      }).set('dayOfMonth', {
        expressionType: NOT_APPLICABLE
      }).set('dayOfWeek', {
        expressionType: VALUES,
        values: [MONDAY]
      }).set('dateProperty', undefined);
    } else if (cadence === MONTHLY) {
      /* default to every 1st of the month */
      return this.set('year', {
        expressionType: EVERY
      }).set('month', {
        expressionType: EVERY
      }).set('dayOfMonth', {
        expressionType: VALUES,
        values: [1]
      }).set('dayOfWeek', {
        expressionType: NOT_APPLICABLE
      }).set('dateProperty', undefined);
    } else {
      /* YEARLY, default to january 1st */
      return this.set('year', {
        expressionType: EVERY
      }).set('month', {
        expressionType: VALUES,
        values: [1]
      }).set('dayOfMonth', {
        expressionType: VALUES,
        values: [1]
      }).set('dayOfWeek', {
        expressionType: NOT_APPLICABLE
      }).set('dateProperty', undefined);
    }
  }
  getCadence() {
    if (this.year && (this.year.expressionType === VALUES || this.year.expressionType === DELEGATE_TO_PROPERTY_VALUE)) {
      return ONCE;
    } else if ((this.dayOfWeek && this.dayOfWeek.expressionType === EVERY || this.dayOfMonth && this.dayOfMonth.expressionType === EVERY) && this.month && this.month.expressionType === EVERY) {
      return DAILY;
    } else if (this.dayOfWeek && this.dayOfWeek.expressionType === VALUES) {
      return WEEKLY;
    } else if (this.dayOfMonth && this.dayOfMonth && (this.month && this.month.expressionType === EVERY || this.getDayOfMonthFlexValue())) {
      return MONTHLY;
    }
    return YEARLY;
  }

  /* This is defined for testing purposes, so that you may mock DayOfMonthFlexValueOptions without relying on `import * as ...`*/
  getDayOfMonthFlexValueOptions() {
    return DayOfMonthFlexValues;
  }

  /* Return possible flex values for monthly cadence. Each flex value option has a fn `isSelected` which returns true when a schedule uses that flex value as its `dayOfMonth`. For example, ON_LAST_DAY.isSelected(schedule) will return true if the schedule argument executes on the last day of the month */
  getDayOfMonthFlexValue() {
    const flexValues = Object.keys(this.getDayOfMonthFlexValueOptions());
    return flexValues.find(key => this.getDayOfMonthFlexValueOptions()[key].isSelected(this));
  }
  setTimeFromMinutes(timeInMinutes) {
    return this.set('hour', {
      expressionType: VALUES,
      values: [Math.floor(timeInMinutes / 60)]
    }).set('minute', {
      expressionType: VALUES,
      values: [timeInMinutes % 60]
    });
  }
  getDayOfWeekValues() {
    return this.dayOfWeek.expressionType === VALUES ? this.dayOfWeek.values : undefined;
  }
  getDayOfMonthValues() {
    return this.dayOfMonth.values;
  }
  getMonthValues() {
    return this.month.values;
  }
  getYearValues() {
    return this.year.values;
  }
  getSimpleDate() {
    if (this.year.expressionType !== VALUES || this.month.expressionType !== VALUES || this.dayOfMonth.expressionType !== VALUES) {
      return null;
    }
    return {
      year: this.year.values[0],
      month: this.month.values[0] - 1,
      date: this.dayOfMonth.values[0]
    };
  }
  getHour() {
    return this.hour.expressionType === VALUES && this.hour.values[0];
  }
  getMinute() {
    return this.minute.expressionType === VALUES && this.minute.values[0];
  }
  getTimeInMinutes() {
    return this.minute.expressionType === VALUES && this.hour.expressionType === VALUES ? this.minute.values[0] + this.hour.values[0] * 60 : undefined;
  }
  getValidationResult() {
    let reason = null;
    if (this.getCadence() === WEEKLY && this.dayOfWeek.expressionType === VALUES && !this.dayOfWeek.values.length) {
      reason = MISSING_DAY_OF_WEEK_FIELD;
    } else if (this.getCadence() === MONTHLY && this.dayOfMonth.expressionType === VALUES && !this.dayOfMonth.values.length) {
      reason = MISSING_DAY_OF_MONTH_FIELD;
    }
    return {
      reason,
      errorMessage: reason && I18n.text(TranslatedErrorMessageKey[reason])
    };
  }

  // Get the default schedule for a portal based on its portal id.
  // A portal should have the same default time for every schedule
  // The default times will range between 6am and 8pm in 15 minute increments
  static getDefaultScheduleForPortal(portalId = 0) {
    const initialHour = 6;
    const lastHour = 20;
    const divisionOfHour = 4; // 4 = 15 min increments
    const incrementsPerHour = divisionOfHour;

    // Put the portal into a group
    const blockNumber = portalId % ((lastHour - initialHour) * divisionOfHour);
    const numberOfHours = initialHour + Math.floor(blockNumber / incrementsPerHour);
    const numberOfMinutes = blockNumber % incrementsPerHour * Math.floor(60 / divisionOfHour);
    return new EnrollmentSchedule({
      minute: {
        expressionType: VALUES,
        values: [numberOfMinutes]
      },
      hour: {
        expressionType: VALUES,
        values: [numberOfHours]
      }
    });
  }

  /* Get default time window based on portal id. A portal should always have the same default for consistancy */
  static getDefaultScheduleWindowForPortal(portalId = 0) {
    const groupNumber = portalId % 3;
    if (groupNumber === 0) {
      return 'BEFORE_BUSINESS_HOURS';
    } else if (groupNumber === 1) {
      return 'AFTER_BUSINESS_HOURS';
    } else {
      return 'DURING_BUSINESS_HOURS';
    }
  }
}